import * as T from 'three';
import {decodeSnapshot} from './wire';
import {closeKind,closeReason,playerMessage} from 'portal-stack/client/snx-match.js';
import { Simulation, BreakMode, SNAP_TICKS, strainTicks } from './simulation';
import { Monkey } from './character';
import type { MoveInput, SimState } from './types';

/** Per-step input history (~2.1 s at 120 Hz) used to replay unacknowledged inputs. */
const HISTORY = 256;
/** Simulation slot used to rebuild the predicted local state (0 player, 1-n troop, 27-30 tools). */
const REPLAY_SLOT = 31;
/** Below this many metres the replayed server state agrees with the local prediction. */
const CORRECT_EPSILON = .25;
/** Corrections larger than this are server teleports (recover, respawn): apply without blending. */
const BLEND_LIMIT = 3;
/** Visual correction offset decay rate (1/s). */
const BLEND_RATE = 12;
/** Snapshots a movement-mode or grip disagreement must persist before it is corrected. */
const DISCRETE_PATIENCE = 3;
const LOAD_RETRY_MS = 5000;
const CONNECT_TIMEOUT_MS = 15000;
const CONNECT_ATTEMPTS = 3;

/** Room match modes (sim/room.hpp MatchMode), keyed by the journal's adventure id. */
export const ROOM_MATCHES: Record<string, number> = { explore: 0, tag: 1, bandits: 2, infection: 3, freeze: 4, team: 5, crown: 6 };
/** Seat roles in the MATCH line (sim/room.hpp Role): Team Tag hunters are chasers, prisoners frozen. */
export const ROLE = { runner: 0, chaser: 1, frozen: 2, crowned: 3 } as const;
/** Team Tag teams (sim/room.hpp teamNames), with the ring/scoreboard colour for each. */
export const TEAMS = [{ name: 'Sunrise', color: '#f5a04a' }, { name: 'Lagoon', color: '#5ec8f2' }] as const;
/** Crown Tag: seconds worn that win outright (sim/room.hpp crownTarget). */
export const CROWN_TARGET = 60;
const MATCH_TITLES = ['', 'Monkey Tag', 'Banana Bandits', 'Infection Tag', 'Freeze Tag', 'Team Tag', 'Crown Tag'];
/** One connected monkey's standing in the room match (the HUD scoreboard). */
export type SeatScore = { id: number; name: string; score: number; role: number; team: number; own: boolean };

type Remote = { monkey: Monkey; state: SimState; view: SimState };
type Pending = { token: number; state: SimState };

function blankState(): SimState {
    return { p: [0, 0, 0], v: [0, 0, 0], anchor: [0, 0, 0], radius: 0, time: 0, mode: 0, grab: -1, candidate: -1, grabs: 0, tails: 0, falls: 0, chain: 0, bestChain: 0, score: 0, flex: 0, autoTails: 0, missReason: 0, occluded: 0, support: -1, gripT: 0, strain: 0, raw: new Array<number>(40).fill(0) };
}
/** Copies forty wire values (starting at `offset`) into an existing state without allocating. */
function fillState(s: SimState, a: ArrayLike<number>, offset = 0) {
    for (let i = 0; i < 40; i++)
        s.raw[i] = a[offset + i];
    const r = s.raw;
    s.p[0] = r[0]; s.p[1] = r[1]; s.p[2] = r[2];
    s.v[0] = r[3]; s.v[1] = r[4]; s.v[2] = r[5];
    s.anchor[0] = r[6]; s.anchor[1] = r[7]; s.anchor[2] = r[8];
    s.radius = r[9]; s.time = r[10]; s.mode = r[15]; s.grab = r[16]; s.candidate = r[18];
    s.grabs = r[19]; s.tails = r[20]; s.falls = r[21]; s.chain = r[22]; s.bestChain = r[23];
    s.score = r[24]; s.flex = r[25]; s.autoTails = r[33]; s.missReason = r[34]; s.occluded = r[35];
    s.support = r[36]; s.gripT = r[37]; s.strain = strainTicks(r) / SNAP_TICKS;
    return s;
}
/**
 * Releases what a remote Monkey owns. SkeletonUtils.clone shares geometry, materials and textures
 * with the cached GLTF in character.ts (also used by the local player), so those are left alone;
 * the cloned skeletons (bone textures), animation mixer bindings and accessory meshes are per-instance.
 */
function disposeMonkey(monkey: Monkey) {
    monkey.root.removeFromParent();
    monkey.mixer.stopAllAction();
    monkey.mixer.uncacheRoot(monkey.model);
    monkey.model.traverse(o => {
        if ((o as T.SkinnedMesh).isSkinnedMesh)
            (o as T.SkinnedMesh).skeleton.dispose();
    });
    monkey.accessory.traverse(o => {
        if (o instanceof T.Mesh) {
            o.geometry.dispose();
            for (const m of Array.isArray(o.material) ? o.material : [o.material])
                (m as T.Material).dispose();
        }
    });
}
/** Exponential backoff with jitter: base·2^n capped, scaled by 50–100 %. */
function backoff(n: number, base = 400, cap = 4000) {
    return Math.min(cap, base * 2 ** n) * (.5 + Math.random() * .5);
}
/** The socket opened but closed before WELCOME: a transient drop worth retrying. */
class DroppedBeforeWelcome extends Error { }

export class Network {
    private socket?: WebSocket;
    private own = -1;
    private sequence = 0;
    private tick = 0;
    private remotes = new Map<number, Remote>();
    private loading = new Map<number, Pending>();
    private retryAt = new Map<number, number>();
    private loadToken = 0;
    private generation = 0;
    /** Bumped only by disconnect(), so connect retries can tell they were cancelled. */
    private epoch = 0;
    private seen = new Set<number>();
    private stateList: SimState[] = [];
    // Ring buffer of every locally simulated step since the last acknowledged input.
    private histSeq = new Int32Array(HISTORY);
    private histX = new Float32Array(HISTORY);
    private histZ = new Float32Array(HISTORY);
    private histButtons = new Uint8Array(HISTORY);
    private histAssist = new Uint8Array(HISTORY);
    private histStart = 0;
    private histCount = 0;
    private replayInput: MoveInput = { x: 0, z: 0, buttons: 0, assist: 0 };
    private serverRaw = new Array<number>(40).fill(0);
    private discreteMismatch = 0;
    /** Visual offset to add to the local monkey's rendered position after a reconciliation correction; decays to zero. */
    readonly correction = new T.Vector3();
    /** An abnormal or backpressure close (1006, 1011, 1013, 1001): the caller resumes the held seat with backoff
     *  (retry: 1013 busy/slow client, wait 5-8 s first; reason: the close's normalized reason). */
    onUnexpectedClose?: (close: {retry: boolean; reason: string | null}) => void;
    /** A terminal close (4409 match_started, 4410 room_released, 1008, "room started/ended"): the seat is gone. */
    onTerminalClose?: (reason: string | null) => void;
    private pingTime = 0;
    roomCode = '';
    connected = false;
    matchMode = 0;
    isIt = false;
    host = -1;
    /** This monkey's role in a tag variant (ROLE), and per-role counts of connected monkeys. */
    role = 0;
    roles = [0, 0, 0, 0];
    /** This monkey's Team Tag team (TEAMS index, -1 none). */
    team = -1;
    /** Team Tag: the hunting team, whole seconds until the hunt swaps, and both team scores. */
    hunters = 0;
    phase = 0;
    teamScores = [0, 0];
    /** Crown Tag: the seat wearing the crown (-1 none). */
    crownHolder = -1;
    /** Every connected seat's standing, in seat order; names come from ROSTER lines. */
    board: SeatScore[] = [];
    private names = new Map<number, string>();
    /** Seconds left in an Infection/Freeze round; -1 while it waits for a second monkey, 0 untimed. */
    clock = 0;
    /** Latest authoritative dead-branch state ([id, downTicks, strainTicks, ...]), applied at reconciliation. */
    private serverBreaks: number[] = [];
    private breaksDirty = false;
    get isTag() { return this.matchMode === 1 || this.matchMode >= 3; }
    /** A Freeze Tag statue or Team Tag prisoner: the room holds this monkey still until a teammate frees it. */
    get frozen() { return this.connected && (this.matchMode === 4 || this.matchMode === 5) && this.role === ROLE.frozen; }
    /** Team colour for a seat's ring (own seat when omitted), or null outside Team Tag. */
    teamColor(id = this.own) {
        if (this.matchMode !== 5) return null;
        const team = id === this.own ? this.team : this.board.find(s => s.id === id)?.team ?? -1;
        return TEAMS[team]?.color ?? null;
    }
    /** Whether a seat (own seat when omitted) wears the Crown Tag crown. */
    crowned(id = this.own) { return this.connected && this.matchMode === 6 && id >= 0 && id === this.crownHolder; }
    nameOf(id: number) { return this.names.get(id) || `Monkey ${id + 1}`; }
    get matchTitle() { return MATCH_TITLES[this.matchMode] || ''; }
    get matchObjective() {
        const clock = this.clock > 0 ? ` · ${Math.floor(this.clock / 60)}:${String(this.clock % 60).padStart(2, '0')}` : '';
        const [runners, chasers, frozen] = this.roles;
        switch (this.matchMode) {
            case 1: return this.isIt ? 'You’re it! Catch another player.' : 'Keep moving. Don’t get caught!';
            case 2: return this.carrying ? `Return the mango to Home Tree · ${this.score} / 3` : `Find a mango at Market Crown · ${this.score} / 3`;
            case 3: return this.clock < 0 ? 'Waiting for another monkey to join…' : this.role === ROLE.chaser ? `You’re infected! Catch the ${runners} still free${clock}` : `Stay free · ${runners} free, ${chasers} infected${clock}`;
            case 4: return this.clock < 0 ? 'Waiting for another monkey to join…' : this.role === ROLE.chaser ? `Freeze every runner · ${frozen} frozen, ${runners} free${clock}` : this.role === ROLE.frozen ? `Frozen! Wait for a friend to touch you${clock}` : `Stay free and touch frozen friends to thaw them · ${frozen} frozen${clock}`;
            case 5: {
                if (this.clock < 0) return 'Waiting for another monkey to join…';
                const mine = TEAMS[this.team]?.name ?? 'Your team', hunters = TEAMS[this.hunters].name, other = TEAMS[1 - this.hunters].name;
                const swap = ` · swap in ${this.phase}s${clock}`;
                return this.role === ROLE.chaser ? `${mine} hunts! Touch ${other} monkeys to jail them${swap}`
                    : this.role === ROLE.frozen ? `Jailed! A ${mine} teammate can free you with a touch${swap}`
                    : `${hunters} is hunting. Stay free and touch jailed teammates to free them${swap}`;
            }
            case 6: return this.clock < 0 ? 'Waiting for another monkey to join…' : this.role === ROLE.crowned ? `You wear the crown! Keep away · ${this.score} / ${CROWN_TARGET} s${clock}` : this.crownHolder >= 0 ? `${this.nameOf(this.crownHolder)} has the crown. Touch them to steal it · ${this.score} / ${CROWN_TARGET} s${clock}` : `Find the crown${clock}`;
            default: return '';
        }
    }
    get isHost() { return this.connected && this.host === this.own; }
    score = 0;
    carrying = false;
    constructor(private sim: Simulation, private scene: T.Scene, private make: () => Promise<Monkey>, private notify: (s: string) => void) { }
    get states() {
        const list = this.stateList;
        list.length = 0;
        for (const r of this.remotes.values())
            list.push(r.state);
        return list;
    }
    get count() { return this.remotes.size; }
    async connect(url: string, name: string): Promise<void> {
        this.disconnect(false);
        const u = new URL(url);
        if (!['ws:', 'wss:'].includes(u.protocol)) throw Error('Use a ws:// or wss:// room address.');
        u.searchParams.set('wire', '3');
        this.notify('Finding your branch…');
        const epoch = this.epoch, deadline = Date.now() + CONNECT_TIMEOUT_MS;
        for (let n = 0; ; n++) {
            try {
                return await this.open(u, name, deadline);
            } catch (error) {
                if (!(error instanceof DroppedBeforeWelcome) || n + 1 >= CONNECT_ATTEMPTS || epoch !== this.epoch)
                    throw error;
                const delay = backoff(n);
                if (Date.now() + delay >= deadline)
                    throw error;
                await new Promise(resolve => setTimeout(resolve, delay));
                if (epoch !== this.epoch)
                    throw Error('The room connection was cancelled.');
            }
        }
    }
    private open(u: URL, name: string, deadline: number): Promise<void> {
        const socket = this.socket = new WebSocket(u);
        socket.binaryType = 'arraybuffer';
        return new Promise((resolve, reject) => {
            let welcomed = false, opened = false;
            const timeout = setTimeout(() => { reject(Error('The room did not answer. Please try again.')); socket.close(); }, Math.max(0, deadline - Date.now()));
            socket.onopen = () => { opened = true; socket.send(`N ${name.replace(/[^a-zA-Z0-9 _-]/g, '').slice(0, 20)}`); };
            // After open, a failure is reported by onclose as a retryable drop.
            socket.onerror = () => { if (opened) return; clearTimeout(timeout); reject(Error('Could not reach that room. Please try again.')); };
            socket.onclose = event => {
                clearTimeout(timeout);
                if (this.socket !== socket) return;
                this.socket = undefined;
                this.generation++; this.connected = false; this.own = -1; this.clear();
                if (!welcomed) reject(opened ? new DroppedBeforeWelcome('The room closed before welcoming you. Please try again.') : Error('That invitation could not be opened. Find the room again.'));
                // Typed closes (ShardNexus closeKind): 1000 is a deliberate end (another window took this seat); terminal closes
                // (1008 policy, 4409/4410, room started/ended) would only repeat (show the reason, find another troop);
                // 1013 backpressure and abnormal drops resume the held seat.
                else if (event.code === 1000) this.notify(event.reason === 'replaced' ? 'This seat continued in another window. Solo play is ready here.' : 'You left the shared branch. Solo play is ready.');
                else {
                    const kind = closeKind(event.code, event.reason), why = closeReason(event.code, event.reason);
                    if (kind === 'terminal') { this.notify(why ? `${playerMessage(why)} Find another troop from the journal.` : `The room closed your connection (${event.reason || 'policy'}). Rejoin from the journal when you are ready.`); this.onTerminalClose?.(why); }
                    else if (this.onUnexpectedClose) this.onUnexpectedClose({retry: kind === 'retry', reason: why});
                    else this.notify('You left the shared branch. Solo play is ready.');
                }
            };
            socket.onmessage = event => {
                if (this.socket !== socket) return;
                try {
                    if (event.data instanceof ArrayBuffer) this.snapshot(decodeSnapshot(event.data));
                    else this.message(String(event.data));
                    if (this.connected && !welcomed) { welcomed = true; clearTimeout(timeout); resolve(); }
                } catch(error) { clearTimeout(timeout); reject(error); socket.close(); }
            };
        });
    }
    disconnect(leave = true) {
        this.generation++;
        this.epoch++;
        if (this.socket) {
            if (leave && this.socket.readyState === WebSocket.OPEN) this.socket.send('L');
            this.socket.onclose = null;
            this.socket.close();
            this.socket = undefined;
        }
        this.connected = false;
        this.roomCode = '';
        this.own = -1;
        this.clear();
    }
    private clear() {
        this.matchMode = 0;
        this.carrying = false;
        this.score = 0;
        this.isIt = false;
        this.host = -1;
        this.role = 0;
        this.roles = [0, 0, 0, 0];
        this.clock = 0;
        this.team = -1;
        this.hunters = this.phase = 0;
        this.teamScores = [0, 0];
        this.crownHolder = -1;
        this.board = [];
        this.names.clear();
        if (this.serverBreaks.length) this.sim.writeBreaks([]);
        this.serverBreaks = [];
        this.breaksDirty = false;
        for (const r of this.remotes.values())
            disposeMonkey(r.monkey);
        this.remotes.clear();
        this.loading.clear();
        this.retryAt.clear();
        this.histStart = this.histCount = 0;
        this.discreteMismatch = 0;
        this.correction.set(0, 0, 0);
    }
    /** Called once per local simulation step with the command about to be stepped. */
    input(input: MoveInput) {
        if (!this.connected || this.socket?.readyState !== WebSocket.OPEN)
            return;
        if (++this.tick % 4 === 0)
            this.socket.send(`I ${++this.sequence} ${input.x.toFixed(4)} ${input.z.toFixed(4)} ${input.buttons} ${input.assist}`);
        // Every step until the next send runs under the latest sent sequence.
        let i: number;
        if (this.histCount < HISTORY)
            i = (this.histStart + this.histCount++) % HISTORY;
        else {
            i = this.histStart;
            this.histStart = (this.histStart + 1) % HISTORY;
        }
        this.histSeq[i] = this.sequence;
        this.histX[i] = input.x;
        this.histZ[i] = input.z;
        this.histButtons[i] = input.buttons;
        this.histAssist[i] = input.assist;
    }
    update(dt: number) {
        this.pingTime += dt;
        if(this.pingTime > 10){this.pingTime = 0; this.command('P');}
        this.correction.multiplyScalar(Math.exp(-dt * BLEND_RATE));
        if (this.correction.lengthSq() < 1e-6)
            this.correction.set(0, 0, 0);
        const k = 1 - Math.exp(-dt * 14);
        for (const [id, r] of this.remotes) {
            const v = r.view, p = v.p, s = r.state.p;
            const x = p[0] + (s[0] - p[0]) * k, y = p[1] + (s[1] - p[1]) * k, z = p[2] + (s[2] - p[2]) * k;
            fillState(v, r.state.raw);
            p[0] = x; p[1] = y; p[2] = z;
            r.monkey.update(dt, v, Math.atan2(v.v[0], v.v[2]));
            r.monkey.mark(this.teamColor(id), this.crowned(id));
        }
    }
    command(kind: string) {
        if (this.connected && this.socket?.readyState === WebSocket.OPEN)
            this.socket.send(kind);
    }
    private message(message: string) {
        if(message.startsWith('ROOM ')){this.roomCode=message.slice(5).trim();return;}
        if (message.startsWith('ROSTER')) {
            // ROSTER seat name|seat name|... (sim/room.hpp roster), sent when names change.
            this.names.clear();
            for (const row of message.slice(7).split('|')) {
                const gap = row.indexOf(' '), id = Number(row.slice(0, gap));
                if (gap > 0 && Number.isInteger(id)) this.names.set(id, row.slice(gap + 1));
            }
            for (const s of this.board) s.name = this.nameOf(s.id);
            return;
        }
        if (message.startsWith('MATCH ')) {
            // MATCH mode it host clock|seat score carrying role team|...|T hunters phase s0 s1|B id down strain ... (sim/room.hpp)
            const rows = message.slice(6).split('|'), head = rows[0].split(' ').map(Number);
            this.matchMode = head[0];
            this.host = head.length > 2 && Number.isFinite(head[2]) ? head[2] : -1;
            this.clock = Number.isFinite(head[3]) ? head[3] : 0;
            this.crownHolder = this.matchMode === 6 && Number.isInteger(head[1]) ? head[1] : -1;
            this.role = 0;
            this.team = -1;
            this.roles = [0, 0, 0, 0];
            const breaks: number[] = [], board: SeatScore[] = [];
            for (const row of rows.slice(1)) {
                if (row.startsWith('B')) {
                    for (const n of row.slice(1).trim().split(' ').map(Number))
                        if (Number.isFinite(n)) breaks.push(n);
                    continue;
                }
                if (row.startsWith('T')) {
                    const t = row.slice(1).trim().split(' ').map(Number);
                    this.hunters = t[0] === 1 ? 1 : 0;
                    this.phase = Number.isFinite(t[1]) ? t[1] : 0;
                    this.teamScores = [Number.isFinite(t[2]) ? t[2] : 0, Number.isFinite(t[3]) ? t[3] : 0];
                    continue;
                }
                const a = row.split(' ').map(Number);
                if (!Number.isInteger(a[0])) continue;
                const role = Number.isInteger(a[3]) && a[3] >= 0 && a[3] <= 3 ? a[3] : 0, team = a[4] === 0 || a[4] === 1 ? a[4] : -1;
                this.roles[role]++;
                board.push({ id: a[0], name: this.nameOf(a[0]), score: Number.isFinite(a[1]) ? a[1] : 0, role, team, own: a[0] === this.own });
                if (a[0] === this.own) {
                    this.score = a[1];
                    this.carrying = !!a[2];
                    this.role = role;
                    this.team = team;
                }
            }
            this.board = board;
            this.isIt = head[1] === this.own || (this.matchMode >= 3 && this.role === ROLE.chaser);
            this.serverBreaks = breaks.slice(0, breaks.length - breaks.length % 3);
            this.breaksDirty = true;
            return;
        }
        if (message.startsWith('WELCOME ')) {
            const [id, version] = message.slice(8).split(' ').map(Number);
            if (version !== this.sim.version) {
                throw Error('The room has updated. Reload CANOPY to join it.');
            }
            this.own = id;
            this.connected = true;
            this.sequence = 0;
            this.tick = 0;
            this.histStart = this.histCount = 0;
            this.discreteMismatch = 0;
            this.notify('You’re on the same branch. Welcome to the troop!');
            return;
        }
        if (message.startsWith('EVENT ')) {
            // The room coalesces a flush's events into one frame: "EVENT a\nEVENT b".
            for (const line of message.split('\n'))
                if (line.startsWith('EVENT ')) this.notify(line.slice(6));
            return;
        }
        if (!message.startsWith('S '))
            return;
        this.snapshot(message.slice(2).split('|').map(row=>row.trim().split(' ').map(Number)));
    }
    /**
     * Server reconciliation. Each snapshot row carries the last input sequence the server applied
     * (ack). Restore the server state into a scratch slot, replay every locally simulated step sent
     * after that ack, and compare with the live prediction. Small disagreements are ignored, larger
     * ones adopt the replayed state and hide the jump behind a decaying visual offset.
     * The server holds each input until the next arrives, so the step count of the acked input is
     * approximate; CORRECT_EPSILON absorbs that.
     */
    private reconcile(row: number[]) {
        const ack = row[1];
        while (this.histCount && this.histSeq[this.histStart] <= ack) {
            this.histStart = (this.histStart + 1) % HISTORY;
            this.histCount--;
        }
        // A Freeze Tag statue is not simulated by the room: adopt its pose as is.
        if (this.frozen)
            this.histStart = this.histCount = 0;
        for (let i = 0; i < 40; i++)
            this.serverRaw[i] = row[2 + i];
        this.sim.restore(this.serverRaw, REPLAY_SLOT);
        // Dead branches are room state: roll the shared world back to the room's, then replay it
        // forward with our own unacknowledged steps so our snaps stay predicted.
        const rollback = this.breaksDirty;
        if (rollback) {
            this.sim.writeBreaks(this.serverBreaks);
            this.breaksDirty = false;
            this.sim.breakMode(REPLAY_SLOT, BreakMode.Live);
        }
        const input = this.replayInput;
        for (let n = 0; n < this.histCount; n++) {
            const i = (this.histStart + n) % HISTORY;
            input.x = this.histX[i]; input.z = this.histZ[i];
            input.buttons = this.histButtons[i]; input.assist = this.histAssist[i];
            if (rollback)
                this.sim.tick();
            this.sim.step(REPLAY_SLOT, input);
        }
        this.sim.breakMode(REPLAY_SLOT, BreakMode.ReadOnly);
        const predicted = this.sim.read(REPLAY_SLOT), current = this.sim.read(0);
        const dx = current.p[0] - predicted.p[0], dy = current.p[1] - predicted.p[1], dz = current.p[2] - predicted.p[2];
        const error = Math.hypot(dx, dy, dz);
        const discrete = predicted.mode !== current.mode || predicted.grab !== current.grab;
        this.discreteMismatch = discrete ? this.discreteMismatch + 1 : 0;
        if (error < CORRECT_EPSILON && this.discreteMismatch < DISCRETE_PATIENCE)
            return;
        this.discreteMismatch = 0;
        this.sim.restore(predicted.raw, 0);
        if (error < BLEND_LIMIT) {
            this.correction.x += dx;
            this.correction.y += dy;
            this.correction.z += dz;
        }
        else
            this.correction.set(0, 0, 0);
    }
    private snapshot(rows:number[][]){
        const seen = this.seen;
        seen.clear();
        for (const nums of rows) {
            if (nums.length < 42)
                continue;
            let finite = true;
            for (let i = 2; i < 42 && finite; i++)
                finite = Number.isFinite(nums[i]);
            if (!finite)
                continue;
            const id = nums[0];
            seen.add(id);
            if (id === this.own) {
                this.reconcile(nums);
                continue;
            }
            const remote = this.remotes.get(id);
            if (remote) {
                fillState(remote.state, nums, 2);
                continue;
            }
            const pending = this.loading.get(id);
            if (pending) {
                fillState(pending.state, nums, 2);
                continue;
            }
            if ((this.retryAt.get(id) ?? 0) > Date.now())
                continue;
            this.load(id, fillState(blankState(), nums, 2));
        }
        for (const [id, r] of this.remotes)
            if (!seen.has(id)) {
                disposeMonkey(r.monkey);
                this.remotes.delete(id);
            }
        for (const id of this.loading.keys())
            if (!seen.has(id))
                this.loading.delete(id);
    }
    private load(id: number, state: SimState) {
        const token = ++this.loadToken, generation = this.generation;
        this.loading.set(id, { token, state });
        this.make().then(monkey => {
            const pending = this.loading.get(id);
            if (!this.connected || generation !== this.generation || pending?.token !== token || this.remotes.has(id)) {
                disposeMonkey(monkey);
                return;
            }
            this.loading.delete(id);
            this.retryAt.delete(id);
            const s = pending.state, view = fillState(blankState(), s.raw);
            monkey.root.position.set(...s.p);
            this.scene.add(monkey.root);
            this.remotes.set(id, { monkey, state: s, view });
        }).catch(error => {
            if (this.loading.get(id)?.token === token)
                this.loading.delete(id);
            // A later snapshot retries once the cool-down passes.
            this.retryAt.set(id, Date.now() + LOAD_RETRY_MS);
            console.warn(`CANOPY: could not load troop member ${id}; retrying in ${LOAD_RETRY_MS / 1000}s.`, error);
        });
    }
}
